/**
 * MessagesArea - область отображения сообщений чата
 *
 * Переиспользуемый компонент для отображения истории сообщений.
 * Используется в ChatPanel и ExpandedPanel.
 */

import { useRef, useEffect, useLayoutEffect, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { MarkdownText } from './MarkdownText';
import { AnimatedDots } from './AnimatedDots';
import { t } from './translations';
import './MessagesArea.css';

// Issue #808 (+ #986): context band (strip above the pinned user message showing the
// last 2-3 lines of the previous assistant reply). 12 % of chat height, clamped
// 44-84 px (4px grid). The +~12 px over the previous 10 %/[32,72] keeps the user's
// just-sent message fully visible with ~half a line of breathing room above it,
// instead of sitting flush against the top of the viewport.
const getContextBand = (clientHeight) => {
  const computed = Math.round(clientHeight * 0.12);
  return Math.min(84, Math.max(44, computed));
};

export const MessagesArea = memo(({
  messages = [],
  emptyText = 'Start a conversation',
  autoScroll = true,
  className = '',
  header = null,
  // Scroll behavior: 'smooth' for new messages, 'instant' for chat open
  initialScroll = false, // true when chat is first opened/reopened
  language = 'en', // for the "typing" indicator aria-label
}) => {
  // Issue #808: Scroll container + spacer refs for "pin user message to top" behaviour
  const containerRef = useRef(null);
  const spacerRef = useRef(null);
  const messagesEndRef = useRef(null);
  // К последнему сообщению крутим САМ список, а не `scrollIntoView`: тот
  // прокручивает всех предков, и у встроенного в страницу чата вместе со
  // списком уезжала вся страница (#1441 — после звонка на витрине окно чата
  // выпрыгивало наверх экрана). У плавающего окна разницы нет: оно `fixed`.
  const scrollToEnd = (behavior) => {
    const container = containerRef.current;
    if (container) container.scrollTo({ top: container.scrollHeight, behavior });
  };
  // Track previous message count to detect new messages vs background load
  const prevMessageCountRef = useRef(0);
  // Issue #808: last rendered message id (to detect which new message arrived)
  const prevLastIdRef = useRef(null);
  // Issue #808: id of the user message currently pinned at top (null = no pin)
  const pinnedUserIdRef = useRef(null);
  // Issue #808: distinguish true first mount from intermediate "count went 0 → N" transitions
  const didMountRef = useRef(false);

  // Filter out system messages from display
  const displayMessages = useMemo(
    () => messages.filter((msg) => msg.role !== 'system'),
    [messages]
  );

  // Initialize prevMessageCountRef on first render
  useEffect(() => {
    if (prevMessageCountRef.current === 0 && displayMessages.length > 0) {
      prevMessageCountRef.current = displayMessages.length;
    }

    // Cleanup on unmount to prevent memory leaks
    return () => {
      prevMessageCountRef.current = 0;
    };
  }, [displayMessages.length]);

  // Issue #808: Anchor-based auto-scroll — pins each new USER message to the top of the
  // visible area so the assistant response renders fully visible below it.
  // React 18 batches the user + streaming-assistant setMessages calls into a single render,
  // so we must detect the new user message by scanning for the latest user-role entry
  // (rather than looking only at the array tail).
  // useLayoutEffect ensures DOM is updated before scrolling (unlike useEffect which is async).
  useLayoutEffect(() => {
    if (!autoScroll) return;

    const currentCount = displayMessages.length;
    const prevCount = prevMessageCountRef.current;
    const container = containerRef.current;
    const endEl = messagesEndRef.current;

    // Latest user message in the current render (if any).
    let lastUserMsg = null;
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      if (displayMessages[i].role === 'user') {
        lastUserMsg = displayMessages[i];
        break;
      }
    }
    const lastMsg = currentCount > 0 ? displayMessages[currentCount - 1] : null;

    // First mount: instant scroll to bottom if messages were preloaded.
    // Record the most recent user message id so we don't falsely re-pin it on next render.
    if (!didMountRef.current) {
      didMountRef.current = true;
      if (currentCount > 0 && endEl) {
        scrollToEnd('instant');
      }
      pinnedUserIdRef.current = lastUserMsg ? lastUserMsg.id : null;
      prevMessageCountRef.current = currentCount;
      prevLastIdRef.current = lastMsg ? lastMsg.id : null;
      return;
    }

    // Chat reopened / reset (message list shrunk): instant scroll to bottom, reset anchor.
    if (prevCount > 0 && currentCount < prevCount) {
      if (spacerRef.current) spacerRef.current.style.minHeight = '0px';
      if (currentCount > 0 && endEl) {
        scrollToEnd('instant');
      }
      pinnedUserIdRef.current = lastUserMsg ? lastUserMsg.id : null;
      prevMessageCountRef.current = currentCount;
      prevLastIdRef.current = lastMsg ? lastMsg.id : null;
      return;
    }

    if (currentCount === 0) {
      pinnedUserIdRef.current = null;
      if (spacerRef.current) spacerRef.current.style.minHeight = '0px';
      prevMessageCountRef.current = 0;
      prevLastIdRef.current = null;
      return;
    }

    // New user message detected → pin it near the top, leaving a dynamic
    // "context band" (12 % of the chat height, clamped 44-84 px — see
    // getContextBand above) so the last 2-3 lines of the previous assistant
    // reply stay visible above. Grow the spacer so the message can physically
    // reach that position even when the assistant reply is still short.
    if (lastUserMsg && lastUserMsg.id !== pinnedUserIdRef.current) {
      pinnedUserIdRef.current = lastUserMsg.id;
      const msgNode = container
        ? container.querySelector(`[data-message-id="${lastUserMsg.id}"]`)
        : null;
      if (container && msgNode && spacerRef.current) {
        const band = getContextBand(container.clientHeight);
        const needed = Math.max(0, container.clientHeight - msgNode.offsetHeight - band);
        spacerRef.current.style.minHeight = `${needed}px`;
        // Double-rAF so the new spacer height is laid out before we compute
        // the final scroll target. offsetTop read inside the second rAF picks
        // up the post-layout position.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const target = Math.max(0, msgNode.offsetTop - band);
            container.scrollTo({ top: target, behavior: 'smooth' });
          });
        });
      } else if (endEl) {
        scrollToEnd('smooth');
      }
      prevMessageCountRef.current = currentCount;
      prevLastIdRef.current = lastMsg.id;
      return;
    }

    // No new user message. Either an assistant message was appended (e.g. greeting)
    // or an existing message is being streamed. If a user message is pinned above,
    // stay put and let the response fill below it; otherwise follow the bottom.
    const isNewLastMsg = lastMsg && lastMsg.id !== prevLastIdRef.current;
    const hasStreaming = displayMessages.some(m => m.isStreaming);
    if (!pinnedUserIdRef.current && endEl && (isNewLastMsg || hasStreaming)) {
      scrollToEnd('smooth');
    }

    prevMessageCountRef.current = currentCount;
    prevLastIdRef.current = lastMsg.id;
  }, [displayMessages, autoScroll]);

  // Issue #808: keep the chat scroll position correct when the container resizes.
  // Two scenarios this addresses:
  // 1. After send, ChatPanel auto-maximizes → container grows → the initial
  //    pin-scroll becomes stale and user msg ends up elsewhere. Re-pin it.
  // 2. When the widget is minimized (display:none) and re-opened, browsers
  //    reset scrollTop to 0 — scroll back to the pinned user message (or to
  //    the bottom if nothing is pinned) so the last visible message isn't lost.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const container = containerRef.current;
    if (!container) return;

    let skipFirst = true;
    let prevClientHeight = container.clientHeight;
    const ro = new ResizeObserver(() => {
      if (skipFirst) {
        skipFirst = false;
        prevClientHeight = container.clientHeight;
        return;
      }
      const currentHeight = container.clientHeight;
      // Detect "widget reopened" — container went from 0 height (display:none)
      // to non-zero. In that case the user expects to see the latest message,
      // not whatever user message was pinned earlier → clear the pin and
      // scroll to bottom.
      const becameVisible = prevClientHeight === 0 && currentHeight > 0;
      prevClientHeight = currentHeight;

      if (becameVisible) {
        pinnedUserIdRef.current = null;
        if (spacerRef.current) spacerRef.current.style.minHeight = '0px';
        if (messagesEndRef.current) {
          scrollToEnd('instant');
        }
        return;
      }

      // Normal resize (e.g. auto-maximize during an active exchange): re-pin
      // the user message so the context band / spacer stay consistent.
      const pinnedId = pinnedUserIdRef.current;
      if (pinnedId) {
        const msgNode = container.querySelector(`[data-message-id="${pinnedId}"]`);
        if (msgNode && spacerRef.current) {
          const band = getContextBand(currentHeight);
          const needed = Math.max(0, currentHeight - msgNode.offsetHeight - band);
          spacerRef.current.style.minHeight = `${needed}px`;
          const target = Math.max(0, msgNode.offsetTop - band);
          container.scrollTo({ top: target, behavior: 'instant' });
        }
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // Pre-compute message states to avoid duplication in filter + map
  const messagesWithState = useMemo(() => {
    const lastId = displayMessages[displayMessages.length - 1]?.id;
    return displayMessages.map((msg, idx) => {
      // Waiting dots appear ONLY after the user sends a message and is awaiting a reply:
      // a streaming agent placeholder with no tokens yet, that is the latest message AND
      // directly follows a user message. The user-message guard means the indicator never
      // shows for the initial greeting/welcome placeholder (no user turn before it), and
      // the latest-message guard keeps it below the user's message, never above.
      const prev = idx > 0 ? displayMessages[idx - 1] : null;
      const isWaiting = msg.role === 'assistant' && msg.isStreaming && !msg.text
        && msg.id === lastId && prev?.role === 'user';
      return {
        ...msg,
        isWaiting,
        // An agent placeholder with no tokens yet has nothing to show — without
        // this it would render as an empty padded bubble until the first token.
        shouldShow: msg.role !== 'assistant' || Boolean(msg.text) || isWaiting,
      };
    });
  }, [displayMessages]);

  return (
    <div ref={containerRef} className={`messages-area ${className}`}>
      {header}
      {messagesWithState.length === 0 ? (
        <div className="messages-area__empty">
          <p>{emptyText}</p>
        </div>
      ) : (
        messagesWithState
          .filter((msg) => msg.shouldShow)
          .map((msg) => {
            return (
              <div
                key={msg.id}
                data-message-id={msg.id}
                className={`messages-area__message messages-area__message--${msg.role}`}
              >
                <div className="messages-area__message-content">
                  {msg.isWaiting ? (
                    <span
                      className="messages-area__typing"
                      role="status"
                      aria-label={t('typing', language)}
                    >
                      <AnimatedDots />
                    </span>
                  ) : (
                    // Markdown is rendered on every token rather than after the
                    // stream settles: the reader never sees raw `**` syntax and
                    // the bubble never reflows from plain text into formatting.
                    <MarkdownText text={msg.text} streaming={msg.isStreaming} />
                  )}
                </div>
              </div>
            );
          })
      )}
      {/* Issue #808: dynamic bottom spacer — grows when a user message is pinned to top */}
      <div ref={spacerRef} className="messages-area__spacer" aria-hidden="true" />
      <div ref={messagesEndRef} />
    </div>
  );
});

MessagesArea.displayName = 'MessagesArea';

MessagesArea.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
    text: PropTypes.string.isRequired,
  })),
  emptyText: PropTypes.string,
  autoScroll: PropTypes.bool,
  className: PropTypes.string,
  header: PropTypes.node,
  language: PropTypes.string,
};
